import { mdiChevronRight, mdiFileDocumentOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fileCabinetCallLoaders } from '../../services/file-cabinet/file-cabinet.call-loaders';
import { fileCabinetDocumentTypeLabel } from '../../services/file-cabinet/file-cabinet.document-types';
import {
    FILE_CABINET_REFERENCE_LIST_NAMES,
    FileCabinetReference,
    FileCabinetReferences,
} from '../../services/file-cabinet/file-cabinet.references';
import {
    CabinetCall,
    CabinetDocument,
    FileCabinetDataSource,
    FileCabinetFileUrls,
} from '../../services/file-cabinet/file-cabinet.types';
import { formatDate } from '../../services/primitives/date-utils';
import { BaseButton } from '../base/button/button';
import { DetailSection, DetailUnavailable } from '../base/detail-panel/detail-panel';
import { PhoneDetails } from '../phone-details/phone-details';
import { referenceIcon, referenceLabel } from './file-cabinet-cells';
import * as S from './file-cabinet.styles';

/**
 * The drawer sections that ask the API for more than the record itself: a document's sources,
 * what was derived from it and what carries it, and a call's recording. Each says, separately,
 * "loading", "none", "not known" and "failed — retry" (NS-3).
 *
 * Email bodies, recordings and transcripts are PHI: their sections carry `data-sentry-block`, so a
 * session replay records a placeholder instead of the content (the default block selector of the
 * installed `@sentry/replay`, whatever `maskAllText` is set to).
 */

/** Long enough for a parent with thousands of children (design: "5k+ can take more than 15 s"). */
const TIMEOUT_MS = 20000;

type Load<T> =
    | { status: 'loading' }
    | { status: 'ready'; value: T }
    /** `pending`: timed out, and the request is still out — its answer is shown if it comes. */
    | { status: 'error'; message: string; timedOut: boolean; pending: boolean };

/**
 * One request for a section, with a timeout and a retry. A stale answer (record switched) is dropped.
 * A timeout does not cancel the request — the data sources take no signal — so Retry waits until it
 * settles: a slow list is not asked again on top of itself.
 */
function useLoad<T>(load: () => Promise<T>, key: string): { state: Load<T>; retry: () => void } {
    const [state, setState] = useState<Load<T>>({ status: 'loading' });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let live = true;
        setState({ status: 'loading' });
        const timer = setTimeout(
            () => live && setState({ status: 'error', message: 'The request timed out.', timedOut: true, pending: true }),
            TIMEOUT_MS,
        );
        load().then(
            value => {
                clearTimeout(timer);
                if (live) {
                    setState({ status: 'ready', value });
                }
            },
            (error: unknown) => {
                clearTimeout(timer);
                if (live) {
                    setState({ status: 'error', message: error instanceof Error ? error.message : String(error), timedOut: false, pending: false });
                }
            },
        );
        return (): void => {
            live = false;
            clearTimeout(timer);
        };
        // `load` is a fresh closure every render; `key` says when it asks for something else.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, attempt]);
    return { state, retry: useCallback(() => setAttempt(a => a + 1), []) };
}

function Loading(): React.ReactElement {
    return <S.Absent>Loading…</S.Absent>;
}

/** `what` is lower case, e.g. "the source documents". */
function Failed({ what, state, retry }: { what: string; state: Load<unknown>; retry: () => void }): React.ReactElement {
    const pending = state.status === 'error' && state.pending;
    const message = pending ? `Loading ${what} is taking longer than usual.` : `Couldn't load ${what}.`;
    return (
        <S.SectionNote>
            <S.Absent>{message}</S.Absent>
            <BaseButton variant="text" size="small" onClick={retry} disabled={pending} aria-label={`Retry loading ${what}`}>
                Retry
            </BaseButton>
        </S.SectionNote>
    );
}

/* ── documents ─────────────────────────────────────────────────── */

export function DocumentRow({ document, onOpen }: { document: CabinetDocument; onOpen: (document: CabinetDocument) => void }): React.ReactElement {
    return (
        <S.DocumentLinkButton type="button" variant="unstyled" onClick={(): void => onOpen(document)}>
            <S.DocumentLink>
                <Icon path={mdiFileDocumentOutline} size={0.75} />
                <S.DocumentLinkText>
                    <S.Primary strong>{document.filename || <S.Absent>No filename</S.Absent>}</S.Primary>
                    <S.Secondary>
                        {[fileCabinetDocumentTypeLabel(document.documentType), document.created && formatDate(document.created, 'MMM d, yyyy')]
                            .filter(Boolean)
                            .join(' · ')}
                    </S.Secondary>
                </S.DocumentLinkText>
                <Icon path={mdiChevronRight} size={0.75} />
            </S.DocumentLink>
        </S.DocumentLinkButton>
    );
}

/** The documents this one was made from — its `sourceDocumentIds`, looked up by `filterId`. */
export function SourceDocuments({
    document,
    dataSource,
    onOpen,
}: {
    document: CabinetDocument;
    dataSource: FileCabinetDataSource;
    onOpen: (document: CabinetDocument) => void;
}): React.ReactElement {
    // Absent from the record is not known; an empty list is "none" (NS-3).
    const known = document.sourceDocumentIds !== undefined;
    const ids = [...new Set(document.sourceDocumentIds ?? [])];
    // The first 100 in one page (the list limit), so a missing one is missing, not on page two.
    const asked = ids.slice(0, 100);
    const { state, retry } = useLoad(
        () =>
            asked.length
                ? dataSource.list('docs', { limit: asked.length, offset: 0, filters: { filterId: [asked.map(id => `EQ:${id}`).join(',')] } })
                : Promise.resolve({ items: [], total: 0 }),
        `source:${asked.join(',')}`,
    );
    return (
        <DetailSection title="Source documents" meta={ids.length ? String(ids.length) : undefined}>
            {!known ? (
                <S.Absent>Not returned with this document.</S.Absent>
            ) : ids.length === 0 ? (
                <S.Absent>Not derived from another document.</S.Absent>
            ) : state.status === 'loading' ? (
                <Loading />
            ) : state.status === 'error' ? (
                <Failed what="the source documents" state={state} retry={retry} />
            ) : (
                <S.ClaimList>
                    {state.value.items.map(source => (
                        <DocumentRow key={source.id} document={source} onOpen={onOpen} />
                    ))}
                    {/* More than one page: say so. Fewer in total than asked for: those were not returned, and
                        why is not known — say how many, never drop them silently. */}
                    {ids.length > asked.length && (
                        <S.Absent>
                            Showing the first {asked.length} of {ids.length}.
                        </S.Absent>
                    )}
                    {state.value.total < asked.length && (
                        <S.Absent>
                            {asked.length - state.value.total} of {asked.length} not returned by the API.
                        </S.Absent>
                    )}
                </S.ClaimList>
            )}
        </DetailSection>
    );
}

/**
 * What was made from this document — `filterSourceDocumentId` (VERIFIED on sandbox, 2026-10-22). Asked
 * when the drawer opens, for this one document only: the design keeps per-row counts out of the table
 * because the total for a large parent is slow.
 */
export function DerivedDocuments({
    document,
    dataSource,
    onOpen,
}: {
    document: CabinetDocument;
    dataSource: FileCabinetDataSource;
    onOpen: (document: CabinetDocument) => void;
}): React.ReactElement {
    const { state, retry } = useLoad(
        () =>
            document.id
                ? dataSource.list('docs', {
                      limit: 25,
                      offset: 0,
                      sort: 'CREATED',
                      order: 'DESCENDING',
                      filters: { filterSourceDocumentId: [`EQ:${document.id}`] },
                  })
                : Promise.resolve({ items: [], total: 0 }),
        `derived:${document.id}`,
    );
    return (
        <DetailSection
            title="Derived documents"
            meta={state.status === 'ready' && state.value.total > 0 ? state.value.total.toLocaleString() : undefined}
        >
            {state.status === 'loading' ? (
                <Loading />
            ) : state.status === 'error' ? (
                <Failed what="the derived documents" state={state} retry={retry} />
            ) : state.value.total === 0 ? (
                <S.Absent>No document was made from this one.</S.Absent>
            ) : (
                <S.ClaimList>
                    {state.value.items.map(derived => (
                        <DocumentRow key={derived.id} document={derived} onOpen={onOpen} />
                    ))}
                    {state.value.total > state.value.items.length && (
                        <S.Absent>
                            Showing {state.value.items.length} of {state.value.total.toLocaleString()}.
                        </S.Absent>
                    )}
                </S.ClaimList>
            )}
        </DetailSection>
    );
}

/**
 * Every mail, letter, fax, email and call that carries this document. `load` is the store's — one
 * loader with "Attached to", so a list that answered 403 there is not asked again here. A list that
 * could not be read is named, so an empty result is never passed off as "referenced by nothing" (NS-3).
 */
export function ReferencedBy({
    document,
    load,
    onOpen,
}: {
    document: CabinetDocument;
    load: (documentIds: string[], limit: number) => Promise<FileCabinetReferences>;
    onOpen: (reference: FileCabinetReference) => void;
}): React.ReactElement {
    const { state, retry } = useLoad(async () => {
        const found = await load(document.id ? [document.id] : [], 25);
        return { references: (document.id && found.byDocument[document.id]) || [], unchecked: found.unchecked, truncated: found.truncated };
    }, `refs:${document.id}`);

    return (
        <DetailSection title="Referenced by">
            {state.status === 'loading' ? (
                <Loading />
            ) : state.status === 'error' ? (
                <Failed what="the records that use this document" state={state} retry={retry} />
            ) : (
                <S.ClaimList>
                    {state.value.references.length === 0 && state.value.unchecked.length === 0 && !state.value.truncated && (
                        <S.Absent>No mail, letter, fax, email or call carries this document.</S.Absent>
                    )}
                    {state.value.references.map(reference => (
                        <S.DocumentLinkButton
                            key={`${reference.view}:${reference.id}`}
                            type="button"
                            variant="unstyled"
                            onClick={(): void => onOpen(reference)}
                        >
                            <S.DocumentLink>
                                <Icon path={referenceIcon(reference.view)} size={0.75} />
                                <S.DocumentLinkText>
                                    <S.Primary>{referenceLabel(reference)}</S.Primary>
                                </S.DocumentLinkText>
                                <Icon path={mdiChevronRight} size={0.75} />
                            </S.DocumentLink>
                        </S.DocumentLinkButton>
                    ))}
                    {state.value.truncated && (
                        <S.Absent>Some lists have more than 25 matches; only the first 25 of each were checked.</S.Absent>
                    )}
                    {state.value.unchecked.length > 0 && (
                        <S.SectionNote>
                            <S.Absent>
                                Not checked:{' '}
                                {state.value.unchecked
                                    .map(u => `${FILE_CABINET_REFERENCE_LIST_NAMES[u.list] ?? u.list}${u.forbidden ? ' (no access)' : ' (failed)'}`)
                                    .join(', ')}
                                .
                            </S.Absent>
                            {/* No access does not change by asking again: Retry only when a list failed. */}
                            {state.value.unchecked.some(u => !u.forbidden) && (
                                <BaseButton variant="text" size="small" onClick={retry} aria-label="Retry the lists not checked">
                                    Retry
                                </BaseButton>
                            )}
                        </S.SectionNote>
                    )}
                    {/* `/phone/ai?filterDocumentId` answers 500 (sandbox, 2026-10-22): say so rather than imply none. */}
                    <S.Absent>AI calls are not searchable by document.</S.Absent>
                </S.ClaimList>
            )}
        </DetailSection>
    );
}

/* ── email, calls ──────────────────────────────────────────────── */

/** The email as sent. `contents` is shown as text — never as HTML. */
export function EmailMessage({ contents }: { contents?: string }): React.ReactElement {
    return (
        <DetailSection title="Message">
            {contents ? <S.MessageBody data-sentry-block>{contents}</S.MessageBody> : <S.Absent>No message body was returned.</S.Absent>}
        </DetailSection>
    );
}

/**
 * The recording and its transcript, through the shared `PhoneDetails` — the same player the claim
 * timeline uses. `urls` is the drawer's own answer for this call's file, not asked a second time.
 *
 * Its loaders are given ones that never throw: `PhoneDetails` awaits the recording and the transcript
 * in one `try/finally` without a `catch`, so a failed transcript (a recording never transcribed) would
 * also lose the audio and leave the section blank. Here each outcome is said under the player on its
 * own: failed, or answered with nothing (NS-3).
 */
export function CallRecording({
    call,
    urls,
    dataSource,
}: {
    call: CabinetCall;
    urls: Promise<FileCabinetFileUrls | undefined>;
    dataSource: FileCabinetDataSource;
}): React.ReactElement {
    const [notes, setNotes] = useState<{ recording?: 'failed' | 'none'; transcript?: 'failed' | 'none' }>({});
    // False once this section is gone (drawer closed, page left): see `fileCabinetCallLoaders`.
    const live = useRef(true);
    useEffect(
        () => (): void => {
            live.current = false;
        },
        [],
    );
    const { downloadDocument, transcribeDocument } = useMemo(
        () =>
            fileCabinetCallLoaders({
                live: () => live.current,
                urls,
                transcription: documentId => dataSource.transcription(documentId),
                note: (what, outcome) => setNotes(n => ({ ...n, [what]: outcome })),
            }),
        [urls, dataSource],
    );
    return (
        <DetailSection title="Recording and transcript">
            {call.document?.id ? (
                // The audio's signed link and the transcript are PHI: blocked from session replay.
                <div data-sentry-block>
                    <PhoneDetails phone={call} downloadDocument={downloadDocument} transcribeDocument={transcribeDocument} />
                    {notes.recording === 'failed' && <S.Absent>Couldn't load the recording.</S.Absent>}
                    {notes.recording === 'none' && <S.Absent>No recording link was returned for this call.</S.Absent>}
                    {notes.transcript === 'failed' && <S.Absent>Couldn't load the transcript.</S.Absent>}
                    {notes.transcript === 'none' && <S.Absent>No transcript was returned for this call.</S.Absent>}
                </div>
            ) : (
                <S.Absent>No recording came with this call.</S.Absent>
            )}
        </DetailSection>
    );
}

/**
 * What the design shows for an AI call that the API does not return. Said once per section, not as a
 * row of "Not available" fields (DetailUnavailable), and not left looking functional (NS-4).
 */
export function AiCallGaps(): React.ReactElement {
    return (
        <>
            <DetailSection title="Claims">
                <DetailUnavailable>
                    The API does not return the claims of an AI call (it can only filter by one). Coming soon.
                </DetailUnavailable>
            </DetailSection>
            <DetailSection title="Recording and transcript">
                <DetailUnavailable>The API does not return a recording or transcript for an AI call. Coming soon.</DetailUnavailable>
            </DetailSection>
        </>
    );
}
